import { WhenAllReady } from "@nimble/react-requests/components/shared/when-all-ready";
import { UseQueryResult } from "@tanstack/react-query";
import { Bookmark } from "lucide-react";
import plur from "plur";

import {
  AlertRow,
  AlertRowStatic,
} from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/alerts/alert-components.view";
import { SAVE_SEARCH_ACTION_LABEL } from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/saved-search-and-preferences/saved-search.constants";

export type SavedSearchAlertRow = {
  uuid: string;
  title: string;
  subtitle: string;
};

const SAVED_SEARCHES_TITLE = "Saved searches";
const LOADING_MESSAGE = "Loading your saved searches…";
const ERROR_MESSAGE = "We couldn't load your saved searches. Try again later.";
const EMPTY_TITLE = "No saved searches yet";

const savedSearchesSubtitle = (count: number): string =>
  `All ${count} ${plur("search", count)}`;

export const SavedSearchesSection = ({
  savedSearches,
  queries,
  checked,
  enabled,
  onChange,
}: {
  savedSearches: SavedSearchAlertRow[];
  queries: UseQueryResult[];
  checked: boolean;
  enabled: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <WhenAllReady
    queries={queries}
    loader={<SavedSearchesLoadingMessage />}
    ErrorWrapper={SavedSearchesErrorMessage}
  >
    {savedSearches.length === 0 ? (
      <SavedSearchesEmptyState />
    ) : (
      <SavedSearchesList
        savedSearches={savedSearches}
        checked={checked}
        enabled={enabled}
        onChange={onChange}
      />
    )}
  </WhenAllReady>
);

const SavedSearchesLoadingMessage = () => (
  <SavedSearchesMessage
    message={LOADING_MESSAGE}
    testId="saved-searches-loading"
  />
);

const SavedSearchesErrorMessage = () => (
  <SavedSearchesMessage message={ERROR_MESSAGE} testId="saved-searches-error" />
);

const SavedSearchesMessage = ({
  message,
  testId,
}: {
  message: string;
  testId: string;
}) => {
  return (
    <p
      className="py-6 text-center text-xs text-ink-subtle"
      data-testid={testId}
    >
      {message}
    </p>
  );
};

const SavedSearchesEmptyState = () => {
  return (
    <div className="py-6 text-center" data-testid="saved-searches-empty">
      <p className="text-sm text-ink-emphasis font-medium">{EMPTY_TITLE}</p>
      <p className="mt-1 text-xs text-ink-faint">
        Apply some filters on the job board and tap{" "}
        <span className="font-medium text-ink-emphasis">
          {SAVE_SEARCH_ACTION_LABEL}
        </span>{" "}
        in the sidebar to start getting alerts.
      </p>
    </div>
  );
};

const SavedSearchesList = ({
  savedSearches,
  checked,
  enabled,
  onChange,
}: {
  savedSearches: SavedSearchAlertRow[];
  checked: boolean;
  enabled: boolean;
  onChange: (checked: boolean) => void;
}) => {
  return (
    <div className="min-w-0" data-testid="saved-searches-list">
      <AlertRow
        title={SAVED_SEARCHES_TITLE}
        subtitle={savedSearchesSubtitle(savedSearches.length)}
        checked={checked}
        enabled={enabled}
        onChange={onChange}
        icon={<Bookmark className="size-4" />}
      />
      <div className="pl-4">
        {savedSearches.map((savedSearch) => (
          <AlertRowStatic
            key={savedSearch.uuid}
            title={savedSearch.title}
            subtitle={savedSearch.subtitle}
            icon={<Bookmark className="size-4" />}
          />
        ))}
      </div>
    </div>
  );
};
